import { Div, Button, router, state, waitFor, IRouteEvent } from '@codesuma/baseline'
import { Slider, ISliderFill } from '../../components/slider'
import { SliderArrows } from '../../components/slider-arrows'
import { BackIcon } from '../../components/back-icon'
import { CenturySlide, CENTURY_SPAN } from './century-slide'
import calendar from '../../services/calendar'
import settings from '../../services/settings'
import { viewShell } from '../../helpers/view-shell'
import { toFaDigits } from '../../helpers/digits'
import style from './index.module.css'

// The years view (route /century?y=<any year in the block>): twenty-five years
// over five columns, the top of the calendar. A horizontal swipe moves a whole
// block, so four of them cross a century.
//
// Same shape as the year page below it, and the same movement: it waits above
// and settles down over what it opened from. See the year page for why that is
// a Div and not a baseline Page.
export const CenturyPage = () => {
    const base = Div()
    base.addClass(style.base)
    base.attr('aria-hidden', 'true')

    // The block at slider index 0, as its first year. Every slide renders the
    // next twenty-five on from the one before.
    let anchor = 1400
    let hasBack = false // false on deep links, where history has no entry to go back to

    const slider = Slider()
    slider.on('fill', ({ slide, index }: ISliderFill) => {
        slide.empty()
        const century = CenturySlide(anchor + index * CENTURY_SPAN)
        century.on('year-open', (y: number) => router.goto(`/year?y=${y}`))
        slide.append(century)
    })
    base.append(slider)

    // The steppers, one block of years at a tap.
    const arrows = SliderArrows(slider, `${toFaDigits(CENTURY_SPAN)} سال`)
    base.append(arrows)

    // Gregorian mode reads LTR, so the slider advances that way too — and the
    // arrows' labels swap sides with it. The page turns with them, which is
    // what carries the dismiss to the corner the day and year pages put it in
    // under the same calendar. This page's own title and grid are pinned LTR
    // in either calendar (see century-slide), so nothing else here moves.
    const syncDirection = () => {
        const ltr = settings.get().system === 'gregorian'
        slider.direction(ltr ? 'ltr' : 'rtl')
        base.attr('dir', ltr ? 'ltr' : 'rtl')
        arrows.sync()
    }

    const dismiss = Button()
    dismiss.addClass(style.dismiss)
    dismiss.attr('aria-label', 'برگشت')
    dismiss.attr('data-ripple', '')
    dismiss.append(BackIcon())
    dismiss.on('click', () => hasBack ? router.back() : router.goto('/'))
    base.append(dismiss)

    // The block this page is currently anchored on; a new one rewinds the slider.
    let anchored = 0

    base.on('enter', async ({ query, from }: IRouteEvent) => {
        const y = Number(query?.y)
        const today = calendar.today()
        const year = Number.isFinite(y) && y > 0
            ? y
            : settings.get().system === 'gregorian' ? today.gy : today.jy
        // The block holding that year, named by its first.
        anchor = Math.floor(year / CENTURY_SPAN) * CENTURY_SPAN

        syncDirection()
        if (anchor !== anchored) {
            anchored = anchor
            if (slider.getCursor() !== 0) slider.reset()
            else slider.fill()
        } else {
            slider.fill()
        }

        hasBack = !!from
        viewShell(true)

        // Commit the resting position (above, faded out) before showing, or the
        // very first navigation has no starting style to transition from.
        void base.el.offsetHeight
        // The year is on its way down; arrive as it finishes, not over the top.
        await waitFor(200)
        base.addClass(style.shown)
        base.attr('aria-hidden', 'false')
    })

    // This view marks the current year, so only Nowruz moves anything on it —
    // but that is one night a year when it can be open and wrong, and it costs
    // a redraw. The anchor block is left alone, as on the year page.
    const refill = () => slider.fill()
    state.on('today', refill)
    base.on('unmounted', () => state.off('today', refill))

    // Nothing sits above this, so it only ever leaves the way it came: back up.
    // The router awaits this, so the year returns only once this has gone. The
    // shell keeps the surface only when the year view is what it hands over to.
    base.on('exit', async ({ to }: IRouteEvent) => {
        base.removeClass(style.shown)
        viewShell(to === '/year')
        await waitFor(160)
        base.attr('aria-hidden', 'true')
    })

    return base
}
